import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useGame } from '../context/GameContext';

const { FiUsers, FiCoffee, FiTrendingUp, FiStar, FiZap, FiClock, FiCheck } = FiIcons;

function BaristaWorkforce() {
  const { state } = useGame();
  const [activeOrders, setActiveOrders] = useState([]);
  const [completedToday, setCompletedToday] = useState(0);

  // Simulate barista work in real-time
  useEffect(() => {
    if (!state.autoBaristas || Object.keys(state.autoBaristas).length === 0) return;

    const interval = setInterval(() => {
      // Generate orders for baristas
      const totalBaristas = Object.values(state.autoBaristas).reduce((sum, barista) => sum + barista.count, 0);
      
      if (totalBaristas > 0 && activeOrders.length < totalBaristas * 2) {
        // Create new order
        const drinks = ['espresso', 'americano', 'latte', 'cappuccino', 'matcha_tea'];
        const randomDrink = drinks[Math.floor(Math.random() * drinks.length)];
        const processingTime = 8000 + Math.random() * 4000; // 8-12 seconds
        
        const newOrder = {
          id: Date.now() + Math.random(),
          drink: randomDrink,
          startTime: Date.now(),
          processingTime,
          assignedBarista: getAvailableBarista(randomDrink),
          status: 'preparing'
        };

        setActiveOrders(prev => [...prev, newOrder]);
      }

      // Update existing orders
      setActiveOrders(prev => prev.map(order => {
        const elapsed = Date.now() - order.startTime;
        if (elapsed >= order.processingTime && order.status === 'preparing') {
          setCompletedToday(count => count + 1);
          return { ...order, status: 'completed' };
        }
        return order;
      }).filter(order => 
        order.status !== 'completed' || Date.now() - order.startTime < order.processingTime + 2000
      ));
    }, 2000);

    return () => clearInterval(interval);
  }, [state.autoBaristas, activeOrders.length]);

  // Find best barista for a drink type
  const getAvailableBarista = (drinkType) => {
    if (!state.autoBaristas) return null;

    // Find baristas that can make this drink
    const availableBaristas = Object.entries(state.autoBaristas)
      .filter(([id, barista]) => {
        const baristaData = getBaristaData(id);
        return barista.count > 0 && baristaData?.specialties.includes(drinkType);
      })
      .map(([id]) => id);

    return availableBaristas.length > 0 ? availableBaristas[0] : null;
  };

  // Get barista type data
  const getBaristaData = (baristaId) => {
    const baristaTypes = {
      trainee: { name: 'Trainee', avatar: '👨‍🎓', specialties: ['espresso', 'americano'] },
      experienced: { name: 'Experienced', avatar: '👩‍💼', specialties: ['espresso', 'americano', 'latte', 'cappuccino'] },
      master: { name: 'Master', avatar: '👨‍🍳', specialties: ['espresso', 'americano', 'latte', 'cappuccino', 'macchiato', 'mocha'] },
      matcha_specialist: { name: 'Matcha Expert', avatar: '🍃', specialties: ['matcha_tea', 'matcha_latte', 'matcha_frappuccino'] },
      speed_demon: { name: 'Speed Demon', avatar: '⚡', specialties: ['espresso', 'americano'] }
    };
    return baristaTypes[baristaId];
  };

  const totalBaristas = Object.values(state.autoBaristas || {}).reduce((sum, barista) => sum + barista.count, 0);

  if (totalBaristas === 0) {
    return (
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg text-center">
        <SafeIcon icon={FiUsers} className="text-4xl text-gray-300 mx-auto mb-2" />
        <h3 className="font-bold text-gray-400 mb-1">No Auto Baristas</h3>
        <p className="text-sm text-gray-500">Hire baristas to see them work here</p>
      </div>
    );
  }

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <SafeIcon icon={FiUsers} className="text-blue-500 mr-2 text-lg" />
          <div>
            <h3 className="font-bold text-gray-800">Barista Workforce</h3>
            <p className="text-xs text-gray-500">Live view of your AI workers</p>
          </div>
        </div>
        <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold">
          {totalBaristas} Active
        </div>
      </div>

      {/* Performance Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-blue-50 p-2 rounded-lg text-center">
          <div className="text-lg font-bold text-blue-600">{activeOrders.length}</div>
          <div className="text-xs text-blue-700">Active Orders</div>
        </div>
        <div className="bg-green-50 p-2 rounded-lg text-center">
          <div className="text-lg font-bold text-green-600">{completedToday}</div>
          <div className="text-xs text-green-700">Completed Today</div>
        </div>
        <div className="bg-purple-50 p-2 rounded-lg text-center">
          <div className="text-lg font-bold text-purple-600">
            {Math.round((completedToday / Math.max(1, completedToday + activeOrders.length)) * 100)}%
          </div>
          <div className="text-xs text-purple-700">Efficiency</div>
        </div>
      </div>

      {/* Active Orders */}
      <div className="space-y-2">
        <h4 className="text-sm font-bold text-gray-700 flex items-center">
          <SafeIcon icon={FiClock} className="mr-1" />
          Current Orders:
        </h4>
        
        <AnimatePresence>
          {activeOrders.slice(0, 4).map(order => {
            const progress = Math.min(100, ((Date.now() - order.startTime) / order.processingTime) * 100);
            const baristaData = getBaristaData(order.assignedBarista);
            
            return (
              <motion.div
                key={order.id}
                className={`bg-gradient-to-r p-3 rounded-xl text-white ${
                  order.status === 'completed' 
                    ? 'from-green-500 to-green-600' 
                    : 'from-blue-500 to-purple-500'
                }`}
                initial={{ opacity: 0, scale: 0.9, x: -20 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.9, x: 20 }}
                layout
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <span className="text-xl mr-2">
                      {order.drink.includes('matcha') ? '🍃' : '☕'}
                    </span>
                    <div>
                      <div className="font-bold text-sm capitalize">
                        {order.drink.replace('_', ' ')}
                      </div>
                      <div className="text-xs opacity-75">
                        by {baristaData?.name} {baristaData?.avatar}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {order.status === 'completed' ? (
                      <SafeIcon icon={FiCheck} className="text-xl" />
                    ) : (
                      <div className="text-sm font-bold">{Math.round(progress)}%</div>
                    )}
                  </div>
                </div>
                
                {order.status !== 'completed' && (
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <motion.div
                      className="bg-white h-2 rounded-full"
                      style={{ width: `${progress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>

        {activeOrders.length === 0 && (
          <div className="text-center py-4 text-gray-400">
            <SafeIcon icon={FiCoffee} className="text-2xl mx-auto mb-1" />
            <p className="text-sm">Waiting for customers...</p>
          </div>
        )}
      </div>

      {/* Team Overview */}
      <div className="mt-4 pt-3 border-t border-gray-200">
        <h4 className="text-sm font-bold text-gray-700 mb-2">Team Composition:</h4>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(state.autoBaristas || {}).map(([baristaId, barista]) => {
            if (barista.count === 0) return null;
            const baristaData = getBaristaData(baristaId);
            
            return (
              <div key={baristaId} className="bg-gray-50 p-2 rounded-lg text-center">
                <div className="text-lg">{baristaData?.avatar}</div>
                <div className="text-xs font-medium text-gray-700">{barista.count}x {baristaData?.name}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default BaristaWorkforce;